import * as React from "react"
import { Col, Hidden, Row } from "react-grid-system"
import { useTranslation } from "react-i18next"
import { dayLabelsColumn, monthLabelsColumn, yearDataColumn, yearLabelsColumn } from "../../../utils/data"
import { radioButtonBarItems } from "../../../utils/design-helpers"
import { SlideOutHeight } from "../../atoms/animations/SlideOutHeight"
import LoadingSpinner from "../../atoms/LoadingSpinner"
import { HorizontalSpacer, Spacer } from "../../atoms/spacers"
import ColumnChart from "./ColumnChart"
import { UsageMetrics } from "../../../utils/models"
import { useTerminalById } from "../../../hooks/useTerminalById"
import dayjs from "dayjs"
import {
  getAllTimeColumnLabels,
  getDataAveragePerSession,
  getDateOfHigestUsage,
  getDatesOnChangedResolution,
  getFromAndToTime,
  getInitialTimeStamps,
  getTimeTitle,
  getTotalDataUsed,
  Resolution,
  RESOLUTIONS,
  usageDataToColumnAllTimeShared,
  usageDataToColumnDataDay,
  usageDataToColumnDataMonth,
  usageDataToColumnDataYear,
} from "../../../utils/chart-helpers"
import TerminalInfoSection from "../../molecules/TerminalInfoSection"
import MetricChip from "../../molecules/MetricChip"
import H4 from "../../atoms/typography/H4"
import Subtitle from "../../atoms/typography/Subtitle"
import RadioButtonBar from "../../molecules/RadioButtonBar"
import H3 from "../../atoms/typography/H3"
import DatePicker from "../../atoms/inputs/DatePicker"
import Checkbox from "../../atoms/inputs/Checkbox"
import styled from "styled-components"
import { captureExceptionWithContext } from "../../../utils/sentry"

interface UsageStatisticsSectionProps {
  detailsData: {
    name: string
    suborganization: string
    id: number | string
    dataplan?: string
    serialNumber: string
    warrantyEndsDate: string
    modemName?: string
    modemSerialNumber: string
    sudoPassword: string
    canEditName?: boolean
    firstDataplanStartDate?: string
  }
  isOpen: boolean
  usageStatistics?: Array<UsageMetrics>
  id?: number | string
  name?: string
}

const DetailsContainer = styled.div`
  padding: ${(props) => props.theme.spacings.large};
`

const UsageStatisticsSection: React.FunctionComponent<UsageStatisticsSectionProps> = ({
  detailsData,
  id,
  name,
  isOpen,
}) => {
  const { now, fromTimestamp, toTimestamp } = getInitialTimeStamps()
  const [clickedSpan, setClickedSpan] = React.useState(false)

  //Initial view - year
  const [fromTime, setFromTime] = React.useState<any>(fromTimestamp)
  const [toTime, setToTime] = React.useState(toTimestamp)

  const [selectedRadio, setSelectedRadio] = React.useState<Resolution>(RESOLUTIONS.year)

  const { terminal, error, loading } =
    id !== undefined
      ? useTerminalById(parseInt(id.toString()), selectedRadio, fromTime, toTime)
      : { terminal: undefined, error: undefined, loading: false }

  const onSelectRadio = (spanId: Resolution) => {
    setSelectedRadio(spanId)
    const { from, to } = getDatesOnChangedResolution(
      fromTime,
      spanId,
      clickedSpan,
      detailsData?.firstDataplanStartDate,
      dayjs(detailsData?.warrantyEndsDate),
    )

    setSpanTitle(getTimeTitle(from, spanId).toString())
    //Adjust resolution based on view
    setFromTime(from)
    setToTime(to)
  }

  if (error) {
    captureExceptionWithContext(error, `error fetching terminal statistics for terminal ${id}`)
  }

  const terminalDataYear = terminal?.totalUsageMetrics && usageDataToColumnDataYear(terminal.totalUsageMetrics)
  const terminalDataMonth = terminal?.totalUsageMetrics && usageDataToColumnDataMonth(terminal.totalUsageMetrics, now)
  const terminalDataDay = terminal?.totalUsageMetrics && usageDataToColumnDataDay(terminal.totalUsageMetrics)
  const terminalDataAllTime =
    terminal?.totalUsageMetrics &&
    usageDataToColumnAllTimeShared(terminal.totalUsageMetrics, detailsData?.firstDataplanStartDate, dayjs())

  const { t } = useTranslation()

  const [columnData, setColumnData] = React.useState(yearDataColumn)
  const [columnLabels, setColumnLabels] = React.useState(yearLabelsColumn)

  React.useMemo(() => {
    if (selectedRadio === RESOLUTIONS.month) {
      setColumnData(terminalDataMonth ?? [])
      setColumnLabels(monthLabelsColumn)
    } else if (selectedRadio === RESOLUTIONS.day) {
      setColumnData(terminalDataDay ?? [])
      setColumnLabels(dayLabelsColumn)
    } else if (selectedRadio === RESOLUTIONS.allTime) {
      const labels = getAllTimeColumnLabels(detailsData?.firstDataplanStartDate, dayjs())
      setColumnData(terminalDataAllTime ?? [])
      setColumnLabels(labels)
    } else {
      setColumnData(terminalDataYear ?? [])
      setColumnLabels(yearLabelsColumn)
    }
  }, [terminal, selectedRadio])

  const [spanTitle, setSpanTitle] = React.useState(getTimeTitle(now, selectedRadio).toString())

  const onSpanBack = () => {
    const newTimeSpan = getFromAndToTime(fromTime, selectedRadio, true)
    setFromTime(newTimeSpan?.from?.toString() ?? fromTime)
    setToTime(newTimeSpan?.to?.toString() ?? toTime)
    const newTimeTitle = getTimeTitle(newTimeSpan?.from, selectedRadio)
    setSpanTitle(newTimeTitle.toString())
    setClickedSpan(true)
  }

  const onSpanForward = () => {
    const newTimeSpan = getFromAndToTime(fromTime, selectedRadio, false)
    setFromTime(newTimeSpan?.from?.toString() ?? fromTime)
    setToTime(newTimeSpan?.to?.toString() ?? toTime)
    const newTimeTitle = getTimeTitle(newTimeSpan?.to, selectedRadio)
    setSpanTitle(newTimeTitle.toString())
    setClickedSpan(true)
  }

  const onSelectFromDate = (value: string) => {
    setFromTime(dayjs(value).format("YYYY-MM-DDTHH:mm:ssZ"))
  }
  const onSelectToDate = (value: string) => {
    setToTime(dayjs(value).format("YYYY-MM-DDTHH:mm:ssZ"))
  }
  const onCheckAllTime = () => {
    setSelectedRadio(RESOLUTIONS.allTime)
    setFromTime(dayjs(detailsData?.firstDataplanStartDate).format("YYYY-MM-DDTHH:mm:ssZ"))
    setToTime(dayjs(detailsData?.warrantyEndsDate).format("YYYY-MM-DDTHH:mm:ssZ"))
  }

  return (
    <SlideOutHeight isVisible={isOpen}>
      <DetailsContainer>
        <HorizontalSpacer spacing={3} />
        <Subtitle textAlign="center">
          {name} {t("details")}
        </Subtitle>
        <HorizontalSpacer spacing={2} />
        <Row justify="center">
          <Col md={7} xs={12}>
            <TerminalInfoSection detailsData={detailsData} />
          </Col>
        </Row>
        {terminal !== undefined && (
          <>
            <HorizontalSpacer spacing={2} />
            <Subtitle textAlign="center">
              {name} {t("statistics")}
            </Subtitle>
            <HorizontalSpacer spacing={3} />
            <Row>
              <Col md={6}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <H3>Show by: </H3>
                  <Spacer spacing={1} />
                  <RadioButtonBar
                    items={radioButtonBarItems}
                    selectedValue={selectedRadio}
                    onClickItem={onSelectRadio}
                  />
                </div>
                <HorizontalSpacer spacing={2} />
                <div style={{ display: "flex", alignItems: "center" }}>
                  <DatePicker
                    onSelectDate={onSelectFromDate}
                    value={dayjs(fromTime).format("YYYY-MM-DD")}
                    disabled={selectedRadio === RESOLUTIONS.allTime}
                    useMinDate={false}
                  />
                  -
                  <DatePicker
                    onSelectDate={onSelectToDate}
                    value={dayjs(toTime).format("YYYY-MM-DD")}
                    disabled={selectedRadio === RESOLUTIONS.allTime}
                    useMinDate={false}
                  />
                  <Hidden xs>
                    <Spacer spacing={1} />
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <Checkbox checked={selectedRadio === RESOLUTIONS.allTime} onChange={onCheckAllTime} />
                      <Spacer spacing={1} /> All time
                    </div>
                  </Hidden>
                </div>
              </Col>
            </Row>

            <HorizontalSpacer spacing={3} />
            <Row justify="around">
              <MetricChip
                value={t("graphs.times_used")}
                icon={<H4 textAlign="center">{terminal?.totalUsageMetrics?.length ?? 0}</H4>}
              />
              <MetricChip
                value="Date of highest used"
                icon={
                  <H4 textAlign="center">
                    {terminal?.totalUsageMetrics && getDateOfHigestUsage(terminal.totalUsageMetrics, selectedRadio)}
                  </H4>
                }
              />
              <MetricChip
                value="Data average per session"
                icon={
                  <H4 textAlign="center">
                    {terminal?.totalUsageMetrics && getDataAveragePerSession(terminal.totalUsageMetrics)}
                  </H4>
                }
              />
              <MetricChip
                value="Total data used"
                icon={
                  <H4 textAlign="center">
                    {terminal?.totalUsageMetrics ? getTotalDataUsed(terminal?.totalUsageMetrics) : 0}
                  </H4>
                }
              />
            </Row>
            <HorizontalSpacer spacing={3} />

            {loading ? <LoadingSpinner /> : <ColumnChart data={columnData} labels={columnLabels} columnWidth={32} />}
          </>
        )}
      </DetailsContainer>
    </SlideOutHeight>
  )
}

export default UsageStatisticsSection
